import { BoxElement, Color, Element, ModelElement, RectCenteredElement } from '@thive/canvas';
import { Obstacle } from '@thive/fddm-types';
import { Vector2, Vector3 } from 'three';

import { assertNever } from '@/helpers/utils';
import { convertV2ToV3 } from '@/modules/common/helpers/math';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';

import { TextureVariant } from '@/modules/visualization/types';
import { SAFETY_BORDER_COLOR, SAFETY_BORDER_WIDTH } from './visualization3DConstants';

const createPillar = (
  id: string,
  name: string,
  position: Vector3,
  rotation: Vector3,
): ModelElement => ({
  id,
  name,
  type: 'Model',
  variant: 'pillar',
  position,
  rotation,
  tags: [FDDM_TAG.MODEL],
});

const createBox = (
  id: string,
  name: string,
  position: Vector3,
  rotation: Vector3,
  scale: Vector3,
  fill: Color,
): BoxElement => ({
  id,
  name,
  type: 'Box',
  position,
  rotation,
  scale,
  fill,
  tags: [FDDM_TAG.MODEL],
});

const createWarningZone = (
  id: string,
  name: string,
  position: Vector3,
  rotation: Vector3,
  size: Vector2,
): RectCenteredElement => ({
  id,
  name,
  type: 'RectCentered',
  position,
  rotation,
  size,
  tags: [FDDM_TAG.MODEL],
  materialModifier: {
    textureVariant: TextureVariant.WarningZone,
  },
});

const createSafetyBorder = (
  obstacle: Obstacle,
  position: Vector3,
  rotation: Vector3,
  size: Vector2,
): RectCenteredElement => ({
  id: `${obstacle.id}-safety-border`,
  name: `safety-${obstacle.name}`,
  type: 'RectCentered',
  position,
  rotation,
  size,
  fill: Color.TRANSPARENT,
  tags: [FDDM_TAG.MODEL],
  stroke: SAFETY_BORDER_COLOR,
  strokeWidth: SAFETY_BORDER_WIDTH / 2,
});

export const createObstacle = (
  obstacle: Obstacle,
  type: Exclude<Obstacle['appearance'], 'WALL'>,
): Element[] => {
  const { id, name, segments } = obstacle;
  const firstSegment = segments[0];
  if (!firstSegment) return [];

  const rectangle = RectangleUtils.fromSegment(firstSegment);
  const { length, width } = rectangle;
  const size = new Vector2(length, width).multiplyScalar(1000);
  const center = new Vector3(rectangle.centerX, rectangle.centerY).multiplyScalar(1000);
  const leftBottomRectangle = RectangleUtils.fddmRectToCanvasLeftBottomRect(rectangle);
  const rotationInDeg = new Vector3(0, 0, leftBottomRectangle.angleInDeg);
  const leftBottomCorner = new Vector3(leftBottomRectangle.x, leftBottomRectangle.y).multiplyScalar(
    1000,
  );

  const safetyBorder = createSafetyBorder(obstacle, center, rotationInDeg, size);

  switch (type) {
    case 'PILLAR':
      return [createPillar(`${id}-pillar`, name, center, rotationInDeg), safetyBorder];
    case 'BOX':
      return [
        createBox(
          `${id}-box`,
          name,
          leftBottomCorner,
          rotationInDeg,
          convertV2ToV3(size, (firstSegment.height ?? 1) * 1000),
          Color.BLUE,
        ),
        safetyBorder,
      ];
    case 'WARNING_ZONE':
      return [
        createWarningZone(`${id}-warning-zone`, name, center, rotationInDeg, size),
        safetyBorder,
      ];
    default:
      assertNever(type);
      return [];
  }
};
